import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

export interface CrossfitUser {
  name: string;
  email: string;
  role: string;
}

interface JWTPayload {
  userId: string;
  email: string;
  name: string;
  role: string;
  exp: number;
}

interface UserContextType {
  user: CrossfitUser | null;
  token: string | null;
  setUser: (user: CrossfitUser | null) => void;
  setToken: (token: string) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<CrossfitUser | null>(null);
  const [token, setTokenState] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing token in localStorage on mount
    const storedToken = localStorage.getItem('crossfit_jwt');
    if (storedToken) {
      try {
        const decoded = jwtDecode<JWTPayload>(storedToken);

        // Check if token is expired
        if (decoded.exp * 1000 > Date.now()) {
          setTokenState(storedToken);
          setUserState({
            name: decoded.name,
            email: decoded.email,
            role: decoded.role,
          });
        } else {
          // Token expired, clear it
          localStorage.removeItem('crossfit_jwt');
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        localStorage.removeItem('crossfit_jwt');
      }
    }
  }, []);

  const setUser = (newUser: CrossfitUser | null) => {
    setUserState(newUser);
  };

  const setToken = (newToken: string) => {
    setTokenState(newToken);
    localStorage.setItem('crossfit_jwt', newToken);

    try {
      const decoded = jwtDecode<JWTPayload>(newToken);
      setUserState({
        name: decoded.name,
        email: decoded.email,
        role: decoded.role,
      });
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  };

  const logout = () => {
    setUserState(null);
    setTokenState(null);
    localStorage.removeItem('crossfit_jwt');
  };

  const isAuthenticated = (): boolean => {
    if (!token) return false;

    try {
      const decoded = jwtDecode<JWTPayload>(token);
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  };

  return (
    <UserContext.Provider value={{ user, token, setUser, setToken, logout, isAuthenticated }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
